<template>
    <section class="litigation-creator">

      <div
        v-if="isCurrentEditor"
        class="editor-owner-strip"
      >
        You are the editor — please save when done.
      </div>
  
      <div class="creator-header">
        <div class="creator-action-row">
          <div class="editor-presence">
            <span
              class="editor-avatar"
              :title="editorName"
              :aria-label="`Current editor: ${editorName}`"
            >
              {{ editorInitials }}
            </span>

            <span>
              {{ editorStatus }}
            </span>
          </div>

          <button
            type="button"
            class="review-button"
            :disabled="saving || !canSetForReview || editingBlocked"
            @click="setForReview"
          >
            Set for Review
          </button>
        </div>
      </div>
  
      <form
        id="litigation-creator-form"
        class="creator-form"
        @submit.prevent="saveDraft"
      >
        <fieldset
          class="edit-fields"
          :disabled="editingBlocked"
        >
        <div class="field-grid">
  
          <label class="full-width">
            Internal Litigation Name
  
            <input
              v-model="internalName"
              type="text"
              autocomplete="off"
              placeholder="Internal matter name"
            />
          </label>

          <label class="full-width">
            Public-Facing Action Name

            <input
              v-model="publicActionName"
              type="text"
              autocomplete="off"
              placeholder="Name shown to App Clients"
            />
          </label>
  
          <label class="full-width">
            Description
  
            <textarea
              v-model="description"
              rows="5"
              placeholder="Provide a plain-language description"
            />
          </label>

          <LitigationSearchMetadataInput
            v-model="tags"
            class="full-width"
            label="Tags"
            placeholder="Consumer products or consumer products, infant health"
            hint="User-facing categories."
            add-button-label="Add"
            confirm-new-values
            highlight-new-values
            :suggestions="tagSuggestions"
            :suggestions-loading="tagSuggestionsLoading"
            :suggestions-error="tagSuggestionsError"
            creator-mode
          />

          <LitigationSearchMetadataInput
            v-model="audienceTags"
            class="full-width"
            label="People and Communities"
            placeholder="Families and caregivers or families and caregivers, veterans"
            hint="Audience groups this Litigation is intended to reach."
            item-name="community"
            add-button-label="Add"
            confirm-new-values
            highlight-new-values
            :suggestions="audienceTagSuggestions"
            :suggestions-loading="audienceTagsLoading"
            :suggestions-error="audienceTagsError"
            creator-mode
          />

          <LitigationSearchMetadataInput
            v-model="searchTerms"
            class="full-width"
            label="Search Terms"
            placeholder="Company name or company name, product, abbreviation"
            hint="Hidden search aliases."
            item-name="search term"
            add-button-label="Add"
            confirm-new-values
            highlight-new-values
            creator-mode
          />
  
          <label class="full-width">
            Potential Expectations
  
            <textarea
              v-model="potentialExpectations"
              rows="5"
              placeholder="Optional information about what users may expect"
            />
          </label>

        </div>
  
        <QuestionBuilder
          title="Required Qualification Questions"
          description="Questions every applicant must answer."
          :questions="qualificationQuestions"
          @add="addQualificationQuestion"
          @remove="removeQualificationQuestion"
          @move="moveQuestion(qualificationQuestions, $event)"
        />
  
        <QuestionBuilder
          title="Personal-Injury Questions"
          description="Questions related to physical or emotional injuries."
          :questions="personalInjuryQuestions"
          @add="addPersonalInjuryQuestion"
          @remove="removePersonalInjuryQuestion"
          @move="moveQuestion(personalInjuryQuestions, $event)"
        />
  
        <QuestionBuilder
          title="Property or Economic-Damage Questions"
          description="Questions related to financial or property damage."
          :questions="economicDamageQuestions"
          @add="addEconomicDamageQuestion"
          @remove="removeEconomicDamageQuestion"
          @move="moveQuestion(economicDamageQuestions, $event)"
        />
  
        <div class="banner-field">
          <label for="litigation-banner-image">
            Banner Image
          </label>

          <div class="banner-input-row">
            <input
              id="litigation-banner-image"
              ref="bannerInput"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              @change="handleFileSelection"
            />

            <button
              v-if="selectedFileName || hasSavedBanner"
              type="button"
              class="remove-banner-button"
              :aria-label="selectedFileName ? 'Clear selected banner image' : 'Remove saved banner image'"
              :title="selectedFileName ? 'Clear selected banner' : 'Remove saved banner'"
              :disabled="saving"
              @click="clearBannerSelection"
            >
              <span class="material-icons-outlined" aria-hidden="true">
                delete_outline
              </span>
            </button>
          </div>

          <span
            v-if="!selectedFileName && hasSavedBanner"
            class="selected-file"
          >
            Banner saved
          </span>

          <span
            v-else-if="bannerRemovalRequested"
            class="selected-file removal-pending"
          >
            Banner will be removed when this draft is saved.
          </span>
        </div>

        </fieldset>

        <p
          v-if="editingBlocked"
          class="editing-message"
        >
          {{ leaseError || `${editorName} is currently editing this litigation. This view is read-only.` }}
        </p>
  
        <p
          v-if="errorMessage"
          class="error-message"
        >
          {{ errorMessage }}
        </p>

        <div
          v-if="litigationId"
          class="litigation-removal"
        >
          <button
            v-if="!removalArmed"
            type="button"
            class="arm-removal-button"
            :disabled="saving || removing || editingBlocked"
            @click="beginRemoval"
          >
            Remove Litigation
          </button>

          <div
            v-else
            class="removal-confirmation"
          >
            <input
              v-model="removalConfirmation"
              type="text"
              autocomplete="off"
              placeholder="Type CONFIRM DELETE"
              aria-label="Type CONFIRM DELETE to remove this litigation"
              :disabled="removing"
              @keyup.enter="removeLitigationDraft"
            />

            <button
              type="button"
              class="confirm-removal-button"
              :disabled="!canConfirmRemoval || removing"
              @click="removeLitigationDraft"
            >
              {{ removing ? "Removing..." : "Submit Removal" }}
            </button>

            <button
              type="button"
              class="cancel-removal-button"
              :disabled="removing"
              @click="cancelRemoval"
            >
              Cancel
            </button>
          </div>
        </div>
  
      </form>

      <div class="save-bar">
        <span class="save-status">
          {{ saveStatus }}
        </span>

        <button
          type="submit"
          form="litigation-creator-form"
          class="save-button"
          :disabled="saving || !isDirty || !canSaveDraft || editingBlocked"
        >
          {{ saving ? "Saving..." : "Save Draft" }}
        </button>
      </div>
  
    </section>
  </template>
  
  <script>
  import {
    computed,
    onMounted,
    ref
  } from "vue";
  
  import {
    deleteLitigation,
    fetchNotificationTagCatalog,
    replaceLitigationBannerFile,
    saveLitigation,
    updateLitigation,
    updateLitigationBanner
  } from "@/firebase/firestore";
  
  import {
    deleteFile
  } from "@/firebase/storage";
  
  import QuestionBuilder from "@/components/litigations/LitigationQuestionBuilder.vue";
  import LitigationSearchMetadataInput from "@/components/litigations/LitigationSearchMetadataInput.vue";
  import { useLitigationEditLease } from "@/composables/useLitigationEditLease";

  import {
    buildLitigationQuestions,
    cleanLitigationQuestionDrafts,
    createLitigationQuestionDraft,
    litigationQuestionDrafts
  } from "@/models/litigationQuestions";
  import {
    normalizeSearchMetadata
  } from "@/models/searchMetadata";
  import { normalizeNotificationTagIds } from "@/models/notificationTags";
  
  export default {
    name:
      "LitigationCreator",
  
    components: {
      QuestionBuilder,
      LitigationSearchMetadataInput
    },

    props: {
      litigation: {
        type:
          Object,
        default:
          null
      },
      adminId: {
        type: String,
        default: ""
      },
      adminName: {
        type: String,
        default: ""
      }
    },
  
    emits: [
      "draft-saved",
      "removed",
      "review-submitted"
    ],
  
    setup(
      props,
      {
        emit
      }
    ) {
  
      const internalName =
        ref(
          props.litigation?.internalName ??
            ""
        );

      const publicActionName =
        ref(
          props.litigation?.publicActionName ??
            ""
        );
  
      const description =
        ref(
          props.litigation?.description ??
            ""
        );

      const tags =
        ref(
          normalizeSearchMetadata(
            props.litigation?.tags
          )
        );

      const tagSuggestions = ref([]);
      const tagSuggestionsLoading = ref(true);
      const tagSuggestionsError = ref("");
      const audienceTags = ref(
        normalizeSearchMetadata(props.litigation?.audienceTags)
      );
      const audienceTagSuggestions = ref([]);
      const audienceTagsLoading = ref(true);
      const audienceTagsError = ref("");

      const loadNotificationCatalog = async () => {
        tagSuggestionsLoading.value = true;
        tagSuggestionsError.value = "";
        audienceTagsLoading.value = true;
        audienceTagsError.value = "";

        try {
          const catalog = await fetchNotificationTagCatalog();
          tagSuggestions.value = catalog
            .filter(tag => tag.category === "legal_topic" && tag.active)
            .map(tag => tag.label);
          const audienceCatalog = catalog.filter(
            tag => tag.category === "audience"
          );
          audienceTagSuggestions.value = audienceCatalog
            .filter(tag => tag.active)
            .map(tag => tag.label);

          if (
            audienceTags.value.length === 0 &&
            normalizeNotificationTagIds(props.litigation?.audienceTagIds).length > 0
          ) {
            const selectedIds = new Set(
              normalizeNotificationTagIds(props.litigation?.audienceTagIds)
            );
            audienceTags.value = audienceCatalog
              .filter(tag => selectedIds.has(tag.id))
              .map(tag => tag.label);
            lastSavedSnapshot.value = formSnapshot.value;
          }
        } catch (error) {
          console.error("Unable to load notification tag suggestions:", error);
          tagSuggestionsError.value = "Saved tag suggestions are unavailable.";
          audienceTagsError.value = "People and Communities are unavailable.";
        } finally {
          tagSuggestionsLoading.value = false;
          audienceTagsLoading.value = false;
        }
      };

      onMounted(loadNotificationCatalog);

      const searchTerms =
        ref(
          normalizeSearchMetadata(
            props.litigation?.searchTerms
          )
        );
  
      const potentialExpectations =
        ref(
          props.litigation?.potentialExpectations ??
            ""
        );

      const existingQualificationQuestions =
        litigationQuestionDrafts(
          props.litigation?.questions,
          "qualification"
        );

      const qualificationQuestions =
        ref(
          existingQualificationQuestions.length > 0 ?
            existingQualificationQuestions :
            [createLitigationQuestionDraft()]
        );
  
      const personalInjuryQuestions =
        ref(
          litigationQuestionDrafts(
            props.litigation?.questions,
            "personalInjury"
          )
        );
  
      const economicDamageQuestions =
        ref(
          litigationQuestionDrafts(
            props.litigation?.questions,
            "economicDamage"
          )
        );
  
      const selectedFile =
        ref(null);
  
      const selectedFileName =
        ref("");

      const bannerInput =
        ref(null);
  
      const saving =
        ref(false);

      const removing =
        ref(false);

      const removalArmed =
        ref(false);

      const removalConfirmation =
        ref("");
  
      const errorMessage =
        ref("");

      const litigationId =
        ref(
          props.litigation?.litigationId ??
            props.litigation?.id ??
            ""
        );

      const {
        editingBlocked,
        editorInitials,
        editorName,
        editorStatus,
        isCurrentEditor,
        leaseError
      } = useLitigationEditLease({
        litigationId,
        adminId: computed(() => props.adminId),
        adminName: computed(() => props.adminName)
      });

      const currentImageUrl =
        ref(
          props.litigation?.imageUrl ??
            ""
        );

      const hasSavedBanner =
        ref(
          currentImageUrl.value !== ""
        );

      const bannerRemovalRequested =
        ref(false);

      const reorderRevision =
        ref(0);

      const formSnapshot =
        computed(() => JSON.stringify({
          internalName:
            internalName.value,
          publicActionName:
            publicActionName.value,
          description:
            description.value,
          tags:
            tags.value,
          audienceTags:
            audienceTags.value,
          searchTerms:
            searchTerms.value,
          potentialExpectations:
            potentialExpectations.value,
          qualificationQuestions:
            qualificationQuestions.value,
          personalInjuryQuestions:
            personalInjuryQuestions.value,
          economicDamageQuestions:
            economicDamageQuestions.value,
          selectedFile:
            selectedFile.value ? {
              name:
                selectedFile.value.name,
              size:
                selectedFile.value.size,
              lastModified:
                selectedFile.value.lastModified
            } :
              null,
          bannerRemovalRequested:
            bannerRemovalRequested.value,
          reorderRevision:
            reorderRevision.value
        }));

      const lastSavedSnapshot =
        ref(formSnapshot.value);

      const isDirty =
        computed(() =>
          formSnapshot.value !==
            lastSavedSnapshot.value
        );
  
      const canSaveDraft =
        computed(() => {
  
          return (
            internalName.value.trim() !==
              "" &&
            publicActionName.value.trim() !==
              "" &&
            description.value.trim() !==
              "" &&
            potentialExpectations.value.trim() !==
              ""
          );
        });

      const canSetForReview =
        computed(() => {

          return (
            canSaveDraft.value &&
            cleanLitigationQuestionDrafts(
              qualificationQuestions.value
            ).length >
              0 &&
            (
              hasSavedBanner.value ||
              selectedFile.value !== null
            )
          );
        });

      const canConfirmRemoval =
        computed(() =>
          removalConfirmation.value === "CONFIRM DELETE" &&
          !editingBlocked.value
        );

      const saveStatus =
        computed(() => {

          if (saving.value) {
            return "Saving draft...";
          }

          if (
            isDirty.value &&
            !canSaveDraft.value
          ) {
            return "Complete the required fields to save.";
          }

          if (isDirty.value) {
            return "Unsaved changes";
          }

          return litigationId.value ?
            "Draft saved" :
            "No changes to save";
        });
  
      const addQualificationQuestion =
        () => {
  
          qualificationQuestions.value.push(
            createLitigationQuestionDraft()
          );
        };
  
      const addPersonalInjuryQuestion =
        () => {
  
          personalInjuryQuestions.value.push(
            createLitigationQuestionDraft()
          );
        };
  
      const addEconomicDamageQuestion =
        () => {
  
          economicDamageQuestions.value.push(
            createLitigationQuestionDraft()
          );
        };
  
      const removeQualificationQuestion =
        index => {
  
          qualificationQuestions.value.splice(
            index,
            1
          );
  
          if (
            qualificationQuestions.value.length ===
            0
          ) {
  
            qualificationQuestions.value.push(
              createLitigationQuestionDraft()
            );
          }
        };
  
      const removePersonalInjuryQuestion =
        index => {
  
          personalInjuryQuestions.value.splice(
            index,
            1
          );
        };
  
      const removeEconomicDamageQuestion =
        index => {
  
          economicDamageQuestions.value.splice(
            index,
            1
          );
        };

      const moveQuestion =
        (
          questions,
          { index, direction }
        ) => {

          const destination =
            index + direction;

          if (
            destination < 0 ||
            destination >= questions.length
          ) {
            return;
          }

          const [question] =
            questions.splice(index, 1);

          questions.splice(
            destination,
            0,
            question
          );

          reorderRevision.value +=
            1;
        };
  
      const handleFileSelection =
        event => {
  
          const file =
            event.target.files?.[0];
  
          selectedFile.value =
            file ?? null;
  
          selectedFileName.value =
            file?.name ?? "";
        };

      const clearBannerSelection =
        () => {

          if (selectedFile.value) {
            selectedFile.value =
              null;

            selectedFileName.value =
              "";

            if (bannerInput.value) {
              bannerInput.value.value =
                "";
            }

            return;
          }

          if (hasSavedBanner.value) {
            hasSavedBanner.value =
              false;

            bannerRemovalRequested.value =
              true;
          }
        };
  
      const litigationData =
        publicationStatus => ({
              schemaVersion:
                1,

              internalName:
                internalName.value.trim(),

              publicActionName:
                publicActionName.value.trim(),
  
              description:
                description.value.trim(),

              tags:
                normalizeSearchMetadata(
                  tags.value
                ),

              audienceTags:
                normalizeSearchMetadata(
                  audienceTags.value
                ),

              searchTerms:
                normalizeSearchMetadata(
                  searchTerms.value
                ),
  
              potentialExpectations:
                potentialExpectations.value.trim() ||
                null,

              imageUrl:
                "",

              questions:
                buildLitigationQuestions({
                  qualificationQuestions:
                    qualificationQuestions.value,
                  personalInjuryQuestions:
                    personalInjuryQuestions.value,
                  economicDamageQuestions:
                    economicDamageQuestions.value
                }),

              publicationStatus:
                publicationStatus
            });

      const persistDraft =
        async () => {

          const draftData =
            litigationData("draft");

            const savedLitigationId =
              litigationId.value ?
                litigationId.value :
                await saveLitigation(
                  draftData
                );

            if (!savedLitigationId) {
  
              throw new Error(
                "Litigation was not saved."
              );
            }

            if (litigationId.value) {

              const updated =
                await updateLitigation(
                  savedLitigationId,
                  draftData
                );

              if (!updated) {

                throw new Error(
                  "Litigation was not updated."
                );
              }

            } else {

              litigationId.value =
                savedLitigationId;
            }
  
            if (
              selectedFile.value
            ) {
  
              const bannerUrl =
                await replaceLitigationBannerFile(
                  savedLitigationId,
                  selectedFile.value
                );

              selectedFile.value =
                null;

              selectedFileName.value =
                "";

              if (bannerInput.value) {
                bannerInput.value.value =
                  "";
              }

              currentImageUrl.value =
                bannerUrl;

              hasSavedBanner.value =
                true;

              bannerRemovalRequested.value =
                false;

            } else if (
              bannerRemovalRequested.value
            ) {

              const bannerUpdated =
                await updateLitigationBanner(
                  savedLitigationId,
                  ""
                );

              if (!bannerUpdated) {
                throw new Error(
                  "Litigation banner reference was not removed."
                );
              }

              currentImageUrl.value =
                "";

              bannerRemovalRequested.value =
                false;
            }

            lastSavedSnapshot.value =
              formSnapshot.value;

            return savedLitigationId;
        };

      const saveDraft =
        async () => {
  
          if (
            !canSaveDraft.value ||
            !isDirty.value ||
            saving.value ||
            editingBlocked.value
          ) {
  
            return;
          }
  
          saving.value =
            true;
  
          errorMessage.value =
            "";
  
          try {

            const savedLitigationId =
              await persistDraft();

            emit(
              "draft-saved",
              savedLitigationId
            );
  
          } catch (error) {
  
            console.error(
              "❌ Unable to save litigation draft:",
              error
            );
  
            errorMessage.value =
              "Unable to save the litigation draft. Please review the fields and try again.";
  
          } finally {
  
            saving.value =
              false;
          }
        };

      const setForReview =
        async () => {

          if (
            !canSetForReview.value ||
            saving.value ||
            editingBlocked.value
          ) {
            return;
          }

          saving.value =
            true;

          errorMessage.value =
            "";

          try {

            const savedLitigationId =
              isDirty.value ||
              !litigationId.value ?
                await persistDraft() :
                litigationId.value;

            const updated =
              await updateLitigation(
                savedLitigationId,
                litigationData("inReview")
              );

            if (!updated) {
              throw new Error(
                "Litigation was not set for review."
              );
            }

            emit(
              "review-submitted",
              savedLitigationId
            );

          } catch (error) {

            console.error(
              "❌ Unable to set litigation for review:",
              error
            );

            errorMessage.value =
              "Unable to set the litigation for review. Please try again.";

          } finally {

            saving.value =
              false;
          }
        };

      const beginRemoval =
        () => {
          if (editingBlocked.value || saving.value) { return; }

          removalConfirmation.value = "";
          removalArmed.value = true;
        };

      const cancelRemoval =
        () => {
          if (removing.value) { return; }

          removalConfirmation.value = "";
          removalArmed.value = false;
        };

      const removeLitigationDraft =
        async () => {
          if (
            !canConfirmRemoval.value ||
            removing.value ||
            !litigationId.value
          ) {
            return;
          }

          removing.value = true;
          errorMessage.value = "";

          try {
            const removed =
              await deleteLitigation(litigationId.value);

            if (!removed) {
              throw new Error("Litigation was not removed.");
            }

            if (currentImageUrl.value) {
              await deleteFile(currentImageUrl.value);
            }

            emit("removed", litigationId.value);
          } catch (error) {
            console.error("❌ Unable to remove litigation:", error);
            errorMessage.value =
              "Unable to remove the litigation. Please try again.";
          } finally {
            removing.value = false;
          }
        };
  
      return {
        internalName,
        publicActionName,
        description,
        tags,
        tagSuggestions,
        tagSuggestionsLoading,
        tagSuggestionsError,
        audienceTags,
        audienceTagSuggestions,
        audienceTagsLoading,
        audienceTagsError,
        searchTerms,
        potentialExpectations,
        litigationId,
        qualificationQuestions,
        personalInjuryQuestions,
        economicDamageQuestions,
        bannerInput,
        selectedFileName,
        hasSavedBanner,
        bannerRemovalRequested,
        saving,
        removing,
        removalArmed,
        removalConfirmation,
        errorMessage,
        editingBlocked,
        editorInitials,
        editorName,
        editorStatus,
        isCurrentEditor,
        leaseError,
        isDirty,
        canSaveDraft,
        canSetForReview,
        canConfirmRemoval,
        saveStatus,
        addQualificationQuestion,
        addPersonalInjuryQuestion,
        addEconomicDamageQuestion,
        removeQualificationQuestion,
        removePersonalInjuryQuestion,
        removeEconomicDamageQuestion,
        moveQuestion,
        handleFileSelection,
        clearBannerSelection,
        beginRemoval,
        cancelRemoval,
        removeLitigationDraft,
        saveDraft,
        setForReview
      };
    }
  };
  
  </script>
  
  <style scoped>
  .litigation-creator {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 22px;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  .editor-owner-strip {
    width: 100%;
    box-sizing: border-box;
    padding: 8px 16px;
    flex-shrink: 0;
    border-radius: 8px;
    background: #18ab69;
    color: #ffffff;
    font-size: 0.86rem;
    font-weight: 700;
    text-align: center;
  }

  .creator-header {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex-shrink: 0;
  }

  .creator-action-row {
    width: 100%;
  }

  .creator-action-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .editor-presence {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #65716b;
    font-size: 0.88rem;
    font-weight: 600;
  }

  .editor-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #eaf6ef;
    color: #128052;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.02em;
  }
  
  .creator-form {
    display: flex;
    flex-direction: column;
    gap: 26px;
    min-height: 0;
    padding-bottom: 104px;
    overflow-y: auto;
  }

  .edit-fields {
    display: contents;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
  }

  .editing-message {
    margin: 0;
    padding: 12px 14px;
    border: 1px solid #efd9a8;
    border-radius: 9px;
    background: #fffaf0;
    color: #8a6721;
  }

  .litigation-removal {
    width: 100%;
    box-sizing: border-box;
    margin-top: 6px;
    padding-top: 24px;
    padding-bottom: 28px;
    border-top: 1px solid #dce4df;
  }

  .arm-removal-button,
  .confirm-removal-button,
  .cancel-removal-button {
    min-height: 42px;
    padding: 10px 15px;
    border-radius: 9px;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
  }

  .arm-removal-button {
    width: 100%;
    border: 1px solid #e2aaaa;
    background: #fff7f7;
    color: #a33636;
  }

  .arm-removal-button:hover:not(:disabled) {
    background: #ffeded;
  }

  .removal-confirmation {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) auto auto;
    gap: 10px;
    width: 100%;
  }

  .removal-confirmation input {
    min-width: 0;
    border-color: #e2aaaa;
  }

  .confirm-removal-button {
    border: 1px solid #a33636;
    background: #a33636;
    color: #ffffff;
  }

  .confirm-removal-button:hover:not(:disabled) {
    background: #8f2d2d;
  }

  .cancel-removal-button {
    border: 1px solid #d7ded9;
    background: #ffffff;
    color: #25322c;
  }

  .cancel-removal-button:hover:not(:disabled) {
    background: #f4f8f5;
  }

  .arm-removal-button:disabled,
  .confirm-removal-button:disabled,
  .cancel-removal-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
  
  .field-grid {
    display: grid;
    grid-template-columns: repeat(
      2,
      minmax(0, 1fr)
    );
    gap: 18px;
  }
  
  .full-width {
    grid-column: 1 / -1;
  }
  
  .creator-form label {
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: #28342e;
    font-weight: 600;
  }

  
  .creator-form input,
  .creator-form textarea,
  .creator-form select {
    width: 100%;
    padding: 12px 13px;
    border: 1px solid #d7ded9;
    border-radius: 9px;
    box-sizing: border-box;
    background: #ffffff;
    color: #1f2a25;
    font: inherit;
  }
  
  .creator-form textarea {
    resize: vertical;
  }
  
  .creator-form input:focus,
  .creator-form textarea:focus,
  .creator-form select:focus {
    border-color: #18ab69;
    outline: 3px solid rgba(24, 171, 105, 0.12);
  }
  
  .banner-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 18px;
    border: 1px solid #e2e7e4;
    border-radius: 12px;
    background: #ffffff;
  }

  .banner-field > label {
    color: #28342e;
    font-weight: 600;
  }

  .banner-input-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .banner-input-row input {
    min-width: 0;
    flex: 1;
  }
  
  .selected-file {
    color: #747e79;
    font-size: 0.85rem;
    font-weight: 400;
  }

  .removal-pending {
    color: #a33636;
  }

  .remove-banner-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    padding: 0;
    border: 1px solid #efc5c5;
    border-radius: 9px;
    background: #fff7f7;
    color: #a33636;
    cursor: pointer;
  }

  .remove-banner-button:hover:not(:disabled) {
    border-color: #e3abab;
    background: #ffeded;
  }

  .remove-banner-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .remove-banner-button .material-icons-outlined {
    font-size: 20px;
  }
  
  .review-button,
  .save-button {
    min-width: 150px;
    padding: 12px 18px;
    border: 0;
    border-radius: 10px;
    color: #ffffff;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    transition:
      background-color 0.18s ease,
      opacity 0.18s ease;
  }
  
  .review-button {
    background: #2f6fda;
  }
  
  .review-button:hover:not(:disabled) {
    background: #285fbc;
  }
  
  .save-button {
    background: #18ab69;
  }
  
  .save-button:hover:not(:disabled) {
    background: #15945c;
  }
  
  .review-button:disabled,
  .save-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .save-bar {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 18px 18px;
    border-top: 1px solid #dce4df;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 -8px 22px rgba(20, 40, 30, 0.08);
    backdrop-filter: blur(10px);
  }

  .save-status {
    color: #65716b;
    font-size: 0.82rem;
    font-weight: 600;
    text-align: center;
  }

  .save-button {
    width: 100%;
  }
  
  .error-message {
    margin: 0;
    padding: 12px 14px;
    border: 1px solid #efc5c5;
    border-radius: 9px;
    background: #fff7f7;
    color: #a33636;
  }
  
  @media (max-width: 700px) {
    .field-grid {
      grid-template-columns: 1fr;
    }
  
    .creator-header {
      align-items: flex-start;
    }

    .creator-action-row {
      align-items: stretch;
      flex-direction: column;
    }

    .removal-confirmation {
      grid-template-columns: 1fr;
    }
  
    .review-button,
    .save-button {
      min-width: 0;
    }
  }
  </style>
